import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, LayoutDashboard, ArrowRight, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const PortalHome = () => {
  const navigate = useNavigate();
  const [agentInfo, setAgentInfo] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('agentToken');
    const info = localStorage.getItem('agentInfo');
    if (!token || !info) { navigate('/agent/login'); return; }
    const parsed = JSON.parse(info);
    // Non-admins go straight to the agent portal
    if (parsed.role !== 'admin' && parsed.role !== 'super_admin') {
      navigate('/agent/dashboard');
      return;
    }
    const init = async () => setAgentInfo(parsed);
    init();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('agentToken');
    localStorage.removeItem('agentInfo');
    toast.success('Logged out successfully');
    navigate('/agent/login');
  };

  if (!agentInfo) return null;

  const cards = [
    {
      key: 'admin',
      title: 'Admin Dashboard',
      desc: 'Manage users, leads, teams, metrics, and company settings.',
      icon: Shield,
      to: '/admin',
      accent: 'bg-brand-blue',
      testid: 'portal-card-admin',
    },
    {
      key: 'agent',
      title: 'Agent Portal',
      desc: 'Your dashboard, weekly activity, goals, reports, and leads.',
      icon: LayoutDashboard,
      to: '/agent/dashboard',
      accent: 'bg-brand-orange',
      testid: 'portal-card-agent',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col" data-testid="portal-home">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-brand-blue rounded-lg flex items-center justify-center font-bold">PC</div>
          <div>
            <div className="font-bold leading-tight">Purposefully Covered</div>
            <div className="text-xs text-white/50">Insurance Group</div>
          </div>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-red-300 hover:text-red-200 transition" data-testid="portal-logout">
          <LogOut size={16} /> Logout
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-full bg-brand-orange flex items-center justify-center text-lg font-bold mx-auto mb-4">
            {agentInfo.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Welcome back, {agentInfo.fullName?.split(' ')[0]}</h1>
          <p className="text-white/60">Where would you like to go?</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-3xl">
          {cards.map(({ key, title, desc, icon: Icon, to, accent, testid }) => (
            <button
              key={key}
              onClick={() => navigate(to)}
              className="group text-left bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl p-8 transition-all duration-200"
              data-testid={testid}
            >
              <div className={`w-14 h-14 ${accent} rounded-xl flex items-center justify-center mb-5`}>
                <Icon size={26} className="text-white" />
              </div>
              <h2 className="text-xl font-bold mb-2">{title}</h2>
              <p className="text-sm text-white/60 mb-6 leading-relaxed">{desc}</p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 group-hover:text-white transition">
                Enter <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortalHome;
